import React, { useMemo } from 'react';
import { Award, Trophy, Star, CheckCircle2, Layers, TrendingUp } from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty, StatusBadge } from './shared';
import { useUserBadges } from '../../hooks/useUserTabs';
import { cn, formatDateTime } from '../../utils/helpers';
import type { UserBadgeItem } from '../../types/userTabs';

// ============================================================================
// Helpers
// ============================================================================

function getTierVariant(tier: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const t = tier.toUpperCase();
  if (t === 'GOLD' || t === 'LEGENDARY') return 'warning';
  if (t === 'PLATINUM' || t === 'DIAMOND') return 'info';
  return 'default';
}

function getTierColor(tier: string): string {
  const t = tier.toUpperCase();
  if (t === 'GOLD' || t === 'LEGENDARY') return 'text-amber-400';
  if (t === 'PLATINUM' || t === 'DIAMOND') return 'text-cyan-400';
  if (t === 'SILVER') return 'text-slate-400';
  if (t === 'BRONZE') return 'text-orange-400';
  return 'text-muted-foreground';
}

// ============================================================================
// Sub-components
// ============================================================================

interface BadgesTabProps {
  userId: number;
}

const ProgressBar: React.FC<{ percent: number }> = ({ percent }) => {
  const clamped = Math.min(100, Math.max(0, percent));
  const isComplete = clamped >= 100;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-muted-foreground">Progress</span>
        <span
          className={cn(
            'text-[10px] font-medium',
            isComplete ? 'text-emerald-400' : 'text-muted-foreground'
          )}
        >
          {Math.round(clamped)}%
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            isComplete
              ? 'bg-emerald-500'
              : clamped >= 75
                ? 'bg-blue-500'
                : clamped >= 50
                  ? 'bg-amber-500'
                  : 'bg-muted-foreground/50'
          )}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
};

const BadgeCard: React.FC<{ item: UserBadgeItem }> = ({ item }) => {
  const { badge, isActive, progressPercent, earnedAt } = item;
  const isEarned = !!earnedAt;

  return (
    <div
      className={cn(
        'rounded-lg border overflow-hidden transition-colors',
        isEarned
          ? 'border-border/50 bg-muted/5'
          : 'border-border/30 bg-muted/5 opacity-75'
      )}
    >
      {/* Badge Visual */}
      <div className="flex items-center justify-center py-5 bg-muted/10 border-b border-border/30">
        {badge.iconUrl ? (
          <img
            src={badge.iconUrl}
            alt={badge.name}
            className="h-12 w-12 object-contain"
          />
        ) : (
          <div
            className={cn(
              'h-12 w-12 rounded-full flex items-center justify-center',
              isEarned ? 'bg-primary/10' : 'bg-muted/20'
            )}
          >
            <Award
              className={cn(
                'h-6 w-6',
                isEarned ? getTierColor(badge.tier) : 'text-muted-foreground/40'
              )}
            />
          </div>
        )}
      </div>

      {/* Badge Info */}
      <div className="p-3 space-y-2.5">
        <div>
          <p className="text-sm font-semibold text-foreground truncate">{badge.name}</p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <StatusBadge
              status={badge.tier}
              variant={getTierVariant(badge.tier)}
            />
            {isActive ? (
              <StatusBadge status="Active" variant="success" />
            ) : (
              <StatusBadge status="Inactive" variant="default" />
            )}
          </div>
        </div>

        {/* Category */}
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Category</p>
          <p className="text-xs text-foreground mt-0.5">{badge.category.replace(/_/g, ' ')}</p>
        </div>

        {/* Progress */}
        <ProgressBar percent={progressPercent} />

        {/* Earned Date */}
        {isEarned ? (
          <div className="flex items-center gap-1.5 pt-1">
            <CheckCircle2 className="h-3 w-3 text-emerald-400 flex-shrink-0" />
            <span className="text-[10px] text-emerald-400">
              Earned {formatDateTime(earnedAt)}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 pt-1">
            <TrendingUp className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />
            <span className="text-[10px] text-muted-foreground">Not yet earned</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const BadgesTab: React.FC<BadgesTabProps> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserBadges(userId, true);

  const earnedCount = useMemo(() => {
    if (!data?.items) return 0;
    return (data.items as UserBadgeItem[]).filter((item) => !!item.earnedAt).length;
  }, [data]);

  const groupedByCategory = useMemo(() => {
    if (!data?.items?.length) return new Map<string, UserBadgeItem[]>();

    const groups = new Map<string, UserBadgeItem[]>();
    const typedItems = data.items as UserBadgeItem[];

    for (const item of typedItems) {
      const category = item.badge.category || 'Uncategorized';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(item);
    }

    // Sort: earned badges first within each category
    groups.forEach((categoryItems) => {
      categoryItems.sort((a: UserBadgeItem, b: UserBadgeItem) => {
        if (a.earnedAt && !b.earnedAt) return -1;
        if (!a.earnedAt && b.earnedAt) return 1;
        return b.progressPercent - a.progressPercent;
      });
    });

    return groups;
  }, [data]);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!data) return <TabEmpty message="No badge data available" />;

  const { items, total } = data;

  if (!items || items.length === 0) {
    return <TabEmpty message="No badges found for this user" icon={Award} />;
  }

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <MetricCard
          label="Total Badges"
          value={total}
          icon={Award}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Earned"
          value={earnedCount}
          icon={Trophy}
          color="text-amber-400"
          bgColor="bg-amber-500/10"
          subtitle={total > 0 ? `${Math.round((earnedCount / total) * 100)}% complete` : undefined}
        />
        <MetricCard
          label="Categories"
          value={groupedByCategory.size}
          icon={Layers}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
        />
      </div>

      {/* Badges grouped by category */}
      {Array.from(groupedByCategory.entries()).map(([category, badges]) => {
        const categoryEarned = badges.filter((b) => !!b.earnedAt).length;

        return (
          <SectionCard
            key={category}
            title={category.replace(/_/g, ' ')}
            icon={Star}
            subtitle={`${categoryEarned} of ${badges.length} earned`}
            collapsible
            defaultOpen
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {badges.map((item, idx) => (
                <BadgeCard key={`${item.badge.name}-${idx}`} item={item} />
              ))}
            </div>
          </SectionCard>
        );
      })}
    </div>
  );
};

export default BadgesTab;
